import {
    IAgentSegment,
    IQueueAgent,
    IQueueCustomer,
    IQueueInformation,
    IQueueReport,
    IQueueSegment,
    IQueueWithChannelsReport,
    IRbacTeam,
} from '@dxp/core/models';

interface IMergeResult<T> {
    changed: boolean;
    value: T[];
}

export const asDate = (
    d: Date | string | undefined | null,
): Date | undefined => {
    if (d == null) return undefined;
    return d instanceof Date ? d : new Date(d);
};

export const dateMs = (d: Date | undefined): number | null => {
    return d ? d.getTime() : null;
};

export const isSameQueueReportShallow = (
    a: IQueueWithChannelsReport,
    b: IQueueWithChannelsReport,
): boolean => {
    return (
        a.id === b.id &&
        a.name === b.name &&
        a.channelId === b.channelId &&
        a.channelType === b.channelType &&
        a.channelGroup === b.channelGroup &&
        a.sla === b.sla &&
        a.abandonThreshold === b.abandonThreshold &&
        a.startOfToday.getTime() === b.startOfToday.getTime() &&
        a.answeredWithinSLARatio === b.answeredWithinSLARatio &&
        a.handledCount === b.handledCount &&
        a.handledWithinSLACount === b.handledWithinSLACount &&
        a.abandonedCount === b.abandonedCount &&
        a.abandonedWithinSLACount === b.abandonedWithinSLACount &&
        a.callbackRequestedCount === b.callbackRequestedCount &&
        a.callbackAcceptedCount === b.callbackAcceptedCount &&
        a.longestWaitTimeInSeconds === b.longestWaitTimeInSeconds &&
        a.avgWaitingTimeInSeconds === b.avgWaitingTimeInSeconds &&
        a.queueTimedOutCount === b.queueTimedOutCount &&
        a.conversationCountForAvgWaitingTime ===
            b.conversationCountForAvgWaitingTime &&
        a.businessUnitId === b.businessUnitId
    );
};

export const isSameChannelReport = (
    a: IQueueReport,
    b: IQueueReport,
): boolean => {
    return (
        a.id === b.id &&
        a.name === b.name &&
        a.channelId === b.channelId &&
        a.channelType === b.channelType &&
        a.channelGroup === b.channelGroup &&
        a.sla === b.sla &&
        a.abandonThreshold === b.abandonThreshold &&
        a.startOfToday.getTime() === b.startOfToday.getTime() &&
        a.answeredWithinSLARatio === b.answeredWithinSLARatio &&
        a.handledCount === b.handledCount &&
        a.handledWithinSLACount === b.handledWithinSLACount &&
        a.abandonedCount === b.abandonedCount &&
        a.abandonedWithinSLACount === b.abandonedWithinSLACount &&
        a.callbackRequestedCount === b.callbackRequestedCount &&
        a.callbackAcceptedCount === b.callbackAcceptedCount &&
        a.longestWaitTimeInSeconds === b.longestWaitTimeInSeconds &&
        a.avgWaitingTimeInSeconds === b.avgWaitingTimeInSeconds &&
        a.queueTimedOutCount === b.queueTimedOutCount &&
        a.conversationCountForAvgWaitingTime ===
            b.conversationCountForAvgWaitingTime &&
        a.businessUnitId === b.businessUnitId
    );
};

export const mergeChannelReports = (
    prev: readonly IQueueReport[] | undefined,
    next: readonly IQueueReport[] | undefined,
): { changed: boolean; value: IQueueReport[] } => {
    const prevArr = prev ?? [];
    const nextArr = next ?? [];

    if (prevArr.length === 0 && nextArr.length === 0) {
        return { changed: false, value: prevArr as IQueueReport[] };
    }

    const prevById = new Map<string, IQueueReport>();
    for (const p of prevArr) prevById.set(p.id, p);

    let changed = prevArr.length !== nextArr.length;
    const merged: IQueueReport[] = new Array(nextArr.length);

    for (let i = 0; i < nextArr.length; i++) {
        const incoming = nextArr[i];
        const existing = prevById.get(incoming.id);

        if (existing && isSameChannelReport(existing, incoming)) {
            merged[i] = existing;
        } else {
            merged[i] = incoming;
            changed = true;
        }
    }

    if (!changed) {
        return { changed: false, value: prevArr as IQueueReport[] };
    }

    return { changed: true, value: merged };
};

export const normalizeQueueSegment = (s: IQueueSegment): IQueueSegment => {
    return {
        ...s,
        queuedAt: asDate(s.queuedAt) as Date,
        deQueuedAt: asDate(s.deQueuedAt),
        startedAt: asDate(s.startedAt) as Date,
        endedAt: asDate(s.endedAt),
    };
};

export const normalizeAgentSegment = (s: IAgentSegment): IAgentSegment => {
    return {
        ...s,
        createdAt: asDate(s.createdAt) as Date,
        closedAt: asDate(s.closedAt),
        startedAt: asDate(s.startedAt) as Date,
        endedAt: asDate(s.endedAt),
        completedAt: asDate(s.completedAt),
    };
};

export const normalizeQueueInformation = (
    i: IQueueInformation,
): IQueueInformation => {
    return {
        ...i,
        queueSegments: (i.queueSegments ?? []).map(normalizeQueueSegment),
        agentSegments: (i.agentSegments ?? []).map(normalizeAgentSegment),
        todaysQueueSegments: (i.todaysQueueSegments ?? []).map(
            normalizeQueueSegment,
        ),
        todaysAgentSegments: (i.todaysAgentSegments ?? []).map(
            normalizeAgentSegment,
        ),
        segments: (i.segments ?? []).map(normalizeQueueSegment),
    };
};

export const isSameCustomer = (
    a: IQueueCustomer,
    b: IQueueCustomer,
): boolean => {
    return (
        a.id === b.id &&
        a.firstName === b.firstName &&
        a.middleName === b.middleName &&
        a.lastName === b.lastName &&
        a.phoneNumber === b.phoneNumber &&
        a.emailAddress === b.emailAddress
    );
};

export const isSameAgent = (a: IQueueAgent, b: IQueueAgent): boolean => {
    return a.id === b.id && a.name === b.name;
};

export const isSameAgentsArray = (
    a: IQueueAgent[] | undefined,
    b: IQueueAgent[] | undefined,
): boolean => {
    const aa = a ?? [];
    const bb = b ?? [];
    if (aa.length !== bb.length) return false;
    for (let i = 0; i < aa.length; i++) {
        if (!isSameAgent(aa[i], bb[i])) return false;
    }
    return true;
};

export const isSameQueueSegment = (
    a: IQueueSegment,
    b: IQueueSegment,
): boolean => {
    return (
        a.queueId === b.queueId &&
        a.queueName === b.queueName &&
        a.conversationId === b.conversationId &&
        a.businessUnitId === b.businessUnitId &&
        a.channelId === b.channelId &&
        a.channelType === b.channelType &&
        a.channelGroup === b.channelGroup &&
        a.routingMode === b.routingMode &&
        a.position === b.position &&
        dateMs(a.queuedAt) === dateMs(b.queuedAt) &&
        dateMs(a.deQueuedAt) === dateMs(b.deQueuedAt) &&
        dateMs(a.startedAt) === dateMs(b.startedAt) &&
        dateMs(a.endedAt) === dateMs(b.endedAt) &&
        isSameCustomer(a.customer, b.customer)
    );
};

export const isSameAgentSegment = (
    a: IAgentSegment,
    b: IAgentSegment,
): boolean => {
    return (
        a.queueId === b.queueId &&
        a.conversationId === b.conversationId &&
        a.status === b.status &&
        dateMs(a.createdAt) === dateMs(b.createdAt) &&
        dateMs(a.closedAt) === dateMs(b.closedAt) &&
        dateMs(a.startedAt) === dateMs(b.startedAt) &&
        dateMs(a.endedAt) === dateMs(b.endedAt) &&
        dateMs(a.completedAt) === dateMs(b.completedAt) &&
        isSameCustomer(a.customer, b.customer) &&
        isSameAgentsArray(a.agents, b.agents)
    );
};

export const isSameQueueInformationShallow = (
    a: IQueueInformation,
    b: IQueueInformation,
): boolean => {
    return (
        a.id === b.id &&
        a.name === b.name &&
        a.workflowId === b.workflowId &&
        a.mediaGroup === b.mediaGroup &&
        a.businessUnitId === b.businessUnitId &&
        a.staffedLoggedInAgentCount === b.staffedLoggedInAgentCount &&
        a.staffedAvailableAgentCount === b.staffedAvailableAgentCount
    );
};

export const mergeByKey = <T>(
    prev: readonly T[] | undefined,
    next: readonly T[] | undefined,
    key: (t: T) => string,
    same: (a: T, b: T) => boolean,
): IMergeResult<T> => {
    const p = prev ?? [];
    const n = next ?? [];

    if (p.length === 0 && n.length === 0)
        return { changed: false, value: p as T[] };

    const prevByKey = new Map<string, T>();
    for (const item of p) prevByKey.set(key(item), item);

    let changed = p.length !== n.length;
    const merged = new Array<T>(n.length);

    for (let i = 0; i < n.length; i++) {
        const incoming = n[i];
        const k = key(incoming);
        const existing = prevByKey.get(k);

        if (existing && same(existing, incoming)) {
            merged[i] = existing;
        } else {
            merged[i] = incoming;
            changed = true;
        }
    }

    if (!changed) return { changed: false, value: p as T[] };
    return { changed: true, value: merged };
};

export const queueSegmentKey = (s: IQueueSegment): string => {
    return `${s.queueId}|${s.conversationId}|${s.startedAt.getTime()}`;
};

export const agentSegmentKey = (s: IAgentSegment): string => {
    return `${s.queueId}|${s.conversationId}|${s.startedAt.getTime()}`;
};

export const deriveQueueIdsFromTeams = (
    teams: IRbacTeam[] | null | undefined,
): string[] => {
    if (!teams?.length) return [];
    return Array.from(new Set(teams.flatMap(t => t.queueIds))).sort();
};

export const areStringArraysEqualUnordered = (
    a: string[],
    b: string[],
): boolean => {
    if (a === b) return true;
    if (a.length !== b.length) return false;

    const setA = new Set(a);
    if (setA.size !== b.length) return false;

    for (const value of b) {
        if (!setA.has(value)) {
            return false;
        }
    }

    return true;
};
